/**
 * VexSpark Wallet Adapter
 *
 * Opens VexSpark at /connect as a compact popup and communicates via postMessage.
 * Session sharing in VexSpark means users won't be re-prompted for password
 * if they've already unlocked in the last 30 minutes.
 */

const WALLET_URL = 'https://wallet.vexspark.com';
const POPUP_WIDTH = 400;
const POPUP_HEIGHT = 560;
const READY_TIMEOUT = 10000; // 10s to wait for popup ready
const RESPONSE_TIMEOUT = 120000; // 2min for user to approve

export class VexSparkAdapter {
  constructor(walletUrl = WALLET_URL) {
    this.walletUrl = walletUrl;
    // 2026-10-31: talk ONLY to the wallet's origin. Requests used to be posted to '*' and every
    // `vexspark:*` message was accepted from ANY window, so another page could answer a sign
    // request with a fake `vexspark:signed` (or read the operations we were about to sign).
    this.walletOrigin = new URL(walletUrl).origin;
    this.popup = null;
    this.address = null;
    this.publicKey = null;
    this.isConnected = false;
    this._pendingRequests = new Map(); // requestId → { resolve, reject }
    this._messageHandler = this._onMessage.bind(this);
    this._ready = false;
    this._readyPromise = null;

    window.addEventListener('message', this._messageHandler);
  }

  /**
   * Connect to VexSpark — opens popup, waits for user approval.
   * @returns {{ address: string, publicKey: string }}
   */
  async connect() {
    this._openPopup();
    await this._waitForReady();

    const requestId = crypto.randomUUID();

    return new Promise((resolve, reject) => {
      this._pendingRequests.set(requestId, { resolve, reject });

      this.popup.postMessage({
        type: 'vexspark:connect',
        requestId,
        origin: window.location.origin,
      }, this.walletOrigin);

      setTimeout(() => {
        if (this._pendingRequests.has(requestId)) {
          this._pendingRequests.delete(requestId);
          reject(new Error('Connection request timed out'));
        }
      }, RESPONSE_TIMEOUT);

      this._pollPopupClosed(requestId);
    });
  }

  /**
   * Send operations to VexSpark for signing and submission.
   * @param {Array} operations — OperationJson[] array
   * @param {{ maxGas?: number, appName?: string, description?: string }} options
   * @returns {string} txHash
   */
  async signAndSubmit(operations, options = {}) {
    if (!this.isConnected) {
      throw new Error('Wallet not connected. Call connect() first.');
    }

    // Reopen popup if it was closed
    if (!this.popup || this.popup.closed) {
      this._openPopup();
      await this._waitForReady();
    }

    // Focus existing popup
    this.popup.focus();

    const requestId = crypto.randomUUID();

    return new Promise((resolve, reject) => {
      this._pendingRequests.set(requestId, { resolve, reject });

      // maxGas is sent ONLY when the caller sets one. Absent, VexSpark prices the
      // bundle per operation (bundle-builder.ts computeMaxGas — e.g. 300K for
      // CreatePool, scaled for composite intents and batches). A flat 100K
      // default here used to override that and under-gas large operations.
      const message = {
        type: 'vexspark:signBundle',
        requestId,
        operations,
        metadata: {
          appName: options.appName || 'VexForge',
          description: options.description,
        },
      };
      if (options.maxGas) message.maxGas = options.maxGas;
      this.popup.postMessage(message, this.walletOrigin);

      setTimeout(() => {
        if (this._pendingRequests.has(requestId)) {
          this._pendingRequests.delete(requestId);
          reject(new Error('Sign request timed out'));
        }
      }, RESPONSE_TIMEOUT);

      this._pollPopupClosed(requestId);
    });
  }

  /**
   * Disconnect — closes popup, clears state.
   */
  disconnect() {
    if (this.popup && !this.popup.closed) {
      try {
        this.popup.postMessage({
          type: 'vexspark:disconnect',
          requestId: crypto.randomUUID(),
        }, this.walletOrigin);
      } catch { /* popup may already be closed */ }
      this.popup.close();
    }
    this.popup = null;
    this.address = null;
    this.publicKey = null;
    this.isConnected = false;
    this._ready = false;
    this._readyPromise = null;

    for (const [, { reject }] of this._pendingRequests) {
      reject(new Error('Wallet disconnected'));
    }
    this._pendingRequests.clear();
  }

  /**
   * Clean up event listener.
   */
  destroy() {
    this.disconnect();
    window.removeEventListener('message', this._messageHandler);
  }

  // --- Internal ---

  _openPopup() {
    if (this.popup && !this.popup.closed) {
      this.popup.focus();
      return;
    }

    const left = Math.round((window.screen.width - POPUP_WIDTH) / 2);
    const top = Math.round((window.screen.height - POPUP_HEIGHT) / 2);

    this.popup = window.open(
      `${this.walletUrl}/connect`,
      'vexspark-wallet',
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=yes,resizable=yes`
    );

    this._ready = false;
    this._readyPromise = null;
  }

  _waitForReady() {
    if (this._ready) return Promise.resolve();

    if (!this._readyPromise) {
      this._readyPromise = new Promise((resolve, reject) => {
        this._readyResolve = resolve;
        setTimeout(() => {
          if (!this._ready) {
            reject(new Error('VexSpark popup did not respond. Is wallet.vexspark.com accessible?'));
          }
        }, READY_TIMEOUT);
      });
    }
    return this._readyPromise;
  }

  _onMessage(event) {
    // Only the wallet popup we opened may answer (event.origin is set by the browser, not forgeable).
    if (event.origin !== this.walletOrigin) return;
    if (this.popup && event.source !== this.popup) return;
    const data = event.data;
    if (!data || typeof data.type !== 'string' || !data.type.startsWith('vexspark:')) return;

    switch (data.type) {
      case 'vexspark:ready':
        this._ready = true;
        if (this._readyResolve) {
          this._readyResolve();
          this._readyResolve = null;
        }
        break;

      case 'vexspark:connected': {
        const pending = this._pendingRequests.get(data.requestId);
        if (pending) {
          this.address = data.address;
          this.publicKey = data.publicKey;
          this.isConnected = true;
          this._pendingRequests.delete(data.requestId);
          pending.resolve({ address: data.address, publicKey: data.publicKey });
          // Auto-close popup after connection — it reopens for signing
          setTimeout(() => { try { this.popup?.close(); } catch {} }, 1500);
        }
        break;
      }

      case 'vexspark:signed': {
        const pending = this._pendingRequests.get(data.requestId);
        if (pending) {
          this._pendingRequests.delete(data.requestId);
          pending.resolve(data.txHash);
        }
        break;
      }

      case 'vexspark:rejected': {
        const pending = this._pendingRequests.get(data.requestId);
        if (pending) {
          this._pendingRequests.delete(data.requestId);
          pending.reject(new Error(data.reason || 'Request rejected by user'));
        }
        break;
      }

      case 'vexspark:error': {
        const pending = this._pendingRequests.get(data.requestId);
        if (pending) {
          this._pendingRequests.delete(data.requestId);
          pending.reject(new Error(data.error || 'Wallet error'));
        }
        break;
      }
    }
  }

  _pollPopupClosed(requestId) {
    const interval = setInterval(() => {
      if (this.popup && this.popup.closed) {
        clearInterval(interval);
        const pending = this._pendingRequests.get(requestId);
        if (pending) {
          this._pendingRequests.delete(requestId);
          pending.reject(new Error('Wallet popup was closed'));
        }
      }
    }, 500);

    setTimeout(() => clearInterval(interval), RESPONSE_TIMEOUT);
  }
}

// Singleton instance
let _adapter = null;

export function getAdapter() {
  if (!_adapter) {
    _adapter = new VexSparkAdapter();
  }
  return _adapter;
}
